import { createContext, useContext, useState, useCallback } from 'react';
import TradenameDialog from '../components/dialogs/TradenameDialog';
import ManufacturerDialog from '../components/dialogs/ManufacturerDialog';
import IngredientDialog from '../components/dialogs/IngredientDialog';

// Create the context
const DialogContext = createContext();

export const useDialog = () => useContext(DialogContext);

export const DialogProvider = ({ children }) => {
  // State for each dialog
  const [tradenameDialog, setTradenameDialog] = useState({
    isOpen: false,
    mode: 'create',
    tradenameUri: null,
    initialIngredients: [],
    initialManufacturers: [],
    onSuccess: null
  });

  const [manufacturerDialog, setManufacturerDialog] = useState({
    isOpen: false,
    mode: 'create',
    manufacturerUri: null,
    onSuccess: null
  });

  const [ingredientDialog, setIngredientDialog] = useState({
    isOpen: false,
    mode: 'create',
    ingredientUri: null,
    onSuccess: null
  });

  // State to hold dialog props for dynamic opening
  const [dialogProps, setDialogProps] = useState({});

  // Helper functions to open each dialog type
  const openTradenameDialog = useCallback((props = {}) => {
    setTradenameDialog({
      isOpen: true,
      mode: props.mode || 'create',
      tradenameUri: props.tradenameUri || props.uri || null,
      initialIngredients: props.initialIngredients || [],
      initialManufacturers: props.initialManufacturers || [],
      onSuccess: props.onSuccess || null
    });
  }, []);

  const closeTradenameDialog = useCallback(() => {
    setTradenameDialog(prev => ({ ...prev, isOpen: false }));
  }, []);

  const openManufacturerDialog = useCallback((props = {}) => {
    setManufacturerDialog({
      isOpen: true,
      mode: props.mode || 'create',
      manufacturerUri: props.manufacturerUri || props.uri || null,
      onSuccess: props.onSuccess || null
    });
  }, []);

  const closeManufacturerDialog = useCallback(() => {
    setManufacturerDialog(prev => ({ ...prev, isOpen: false }));
  }, []);

  const openIngredientDialog = useCallback((props = {}) => {
    setIngredientDialog({
      isOpen: true,
      mode: props.mode || 'create',
      ingredientUri: props.ingredientUri || props.uri || null,
      onSuccess: props.onSuccess || null
    });
  }, []);

  const closeIngredientDialog = useCallback(() => {
    setIngredientDialog(prev => ({ ...prev, isOpen: false }));
  }, []);

  // Generic dialog opener function
  const openDialog = useCallback((dialogType, props) => {
    switch(dialogType) {
      case 'tradename':
        openTradenameDialog(props || {});
        break;
      case 'manufacturer':
        openManufacturerDialog(props || {});
        break;
      case 'ingredient':
        openIngredientDialog(props || {});
        break;
      default:
        console.error(`Dialog type "${dialogType}" not recognized`);
    }
  }, [openTradenameDialog, openManufacturerDialog, openIngredientDialog]);

  // Handle success for each dialog
  const handleTradenameSuccess = useCallback((data) => {
    if (tradenameDialog.onSuccess) {
      tradenameDialog.onSuccess(data);
    }
    closeTradenameDialog();
  }, [tradenameDialog.onSuccess, closeTradenameDialog]);

  const handleManufacturerSuccess = useCallback((data) => {
    if (manufacturerDialog.onSuccess) {
      manufacturerDialog.onSuccess(data);
    }
    closeManufacturerDialog();
  }, [manufacturerDialog.onSuccess, closeManufacturerDialog]);

  const handleIngredientSuccess = useCallback((data) => {
    if (ingredientDialog.onSuccess) {
      ingredientDialog.onSuccess(data);
    }
    closeIngredientDialog();
  }, [ingredientDialog.onSuccess, closeIngredientDialog]);

  // Provide all values and functions to children
  const contextValue = {
    openTradenameDialog,
    openManufacturerDialog,
    openIngredientDialog,
    closeTradenameDialog,
    closeManufacturerDialog,
    closeIngredientDialog,
    openDialog
  };

  return (
    <DialogContext.Provider value={contextValue}>
      {children}

      {/* Render dialogs outside of main content but within provider */}
      <TradenameDialog
        isOpen={tradenameDialog.isOpen}
        onClose={closeTradenameDialog}
        onSuccess={handleTradenameSuccess}
        mode={tradenameDialog.mode}
        tradenameUri={tradenameDialog.tradenameUri}
        initialIngredients={tradenameDialog.initialIngredients}
        initialManufacturers={tradenameDialog.initialManufacturers}
      />

      <ManufacturerDialog
        isOpen={manufacturerDialog.isOpen}
        onClose={closeManufacturerDialog}
        onSuccess={handleManufacturerSuccess}
        mode={manufacturerDialog.mode}
        manufacturerUri={manufacturerDialog.manufacturerUri}
      />

      <IngredientDialog
        isOpen={ingredientDialog.isOpen}
        onClose={closeIngredientDialog}
        onSuccess={handleIngredientSuccess}
        mode={ingredientDialog.mode}
        ingredientUri={ingredientDialog.ingredientUri}
      />
    </DialogContext.Provider>
  );
};

export default DialogContext; 